import { gql } from "@apollo/client";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
} from "@mui/material";
import { useState } from "react";
import {
  useCancelVisitDialogQuery,
  useCancelVisitMutation,
} from "~/graphql/__generated__/graphql";
import LabeledSelect from "../common/forms/LabeledSelect";

type Props = {
  visitId: number;
  onClose: () => void;
  onSuccess: () => void;
};

const CancelVisitDialog = ({
  visitId,
  onClose,
  onSuccess,
}: Props) => {
  const [cancelMutation, { loading: submitting }] = useCancelVisitMutation({
    refetchQueries: ['CommandIndexCombinedData', 'VisitLocations'],
  });

  const onConfirm = async (visitCancellationReasonId: number) => {
    await cancelMutation({
      variables: {
        visitId,
        visitCancellationReasonId,
      },
    });
    onSuccess();
  };

  const { data, loading } = useCancelVisitDialogQuery({
    variables: { visitId },
    fetchPolicy: "cache-and-network",
    skip: !visitId,
  });
  const visit = data?.visitSingle;

  const reasons = data?.visitCancellationReason;

  const [cancelReasonId, setCancelReasonId] = useState<string>("");

  const canSubmit = cancelReasonId && !submitting;

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>
        {visit?.patient
          ? `Cancel visit with ${[
              visit?.patient.firstName,
              visit?.patient.lastName,
            ].join(" ")}?`
          : "Cancel visit?"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to cancel this visit?
        </DialogContentText>
        <LabeledSelect
          value={cancelReasonId}
          fullWidth
          sx={{ mt: 2 }}
          size="small"
          label="Cancellation Reason"
          onChange={(event) => setCancelReasonId(event.target.value)}
          disabled={loading}
        >
          {reasons?.map((reason) => (
            <MenuItem key={reason.id} value={reason.id}>
              {reason.description}
            </MenuItem>
          ))}
        </LabeledSelect>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Close
        </Button>
        <Button
          onClick={() => canSubmit && onConfirm(Number(cancelReasonId))}
          color="error"
          variant="contained"
          disabled={!canSubmit}
        >
          Yes, Cancel Visit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
export default CancelVisitDialog;

gql`
  query CancelVisitDialog($visitId: Int!)
  @permission(requires: manageAnyVisit, check: [userInVisitServiceLine])
  {
    visitSingle(where: { id: { eq: $visitId } }) {
      id
      patient {
        firstName
        lastName
      }
    }
    visitCancellationReason(where: { reasonType: { eq: cancel }, active: { eq: true } }) {
      id
      organizationId
      description
      createdAt
      reasonType
      createdBy
    }
  }
`;

gql`
  mutation CancelVisit($visitId: Int!, $visitCancellationReasonId: Int!)
  @permission(requires: manageAnyVisit, check: [userInVisitServiceLine])
  {
    cancelVisit(
      visitId: $visitId
      visitCancellationReasonId: $visitCancellationReasonId
    ) {
      id
      eventStatus
      visitCancellationReasonId
    }
  }
`;
